import { ActionContext } from "vuex";
import { RootState } from "../../store";
import { PlanTrailState } from "../PlanTrailModule";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { hiikerElevation } from "../../../../../utils/helpers/HiikerElevationApiHelper";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { debounce } from "../../../../../utils/helpers/debounce";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { MoveMarkerCommand } from "../../undo/commands/MoveMarkerCommand";
import { MoveConnectorCommand } from "../../undo/commands/MoveConnectorCommand";
import { DisconnectStagesCommand } from "../../undo/commands/DisconnectStagesCommand";
import { ReconnectStagesCommand } from "../../undo/commands/ReconnectStagesCommand";
import { haversineMeters } from "../../../services/stage-connectors";
import { ON_TRAIL_M, MeasuredLine } from "../../../utils/trailLine";
import { getMap } from "./types";
import { cropDetourAtTrail } from "../../../services/navigable-gate";
import { dayFlagLine, nearestAlongLine } from "../../../services/day-flags";
import { askToAddDetour } from "../../../utils/detourPrompt";
import { bridgeDays } from "./day-flags";
import { RouteSnapshotsCommand } from "../../undo/commands/RouteSnapshotsCommand";
import { announceRouteChanged } from "../../../utils/routeChangedNotice";
import { i18n } from "../../../../../i18n";

type ActionContextType = ActionContext<PlanTrailState, RootState>;

// Debounce delay for API calls during drag
const DRAG_PREVIEW_DEBOUNCE_MS = 500;

// Create preview layers on the map
function createPreviewLayers() {
  console.log('[DRAG_PREVIEW] createPreviewLayers called');
  if (!window.map) {
    console.log('[DRAG_PREVIEW] window.map not available');
    return;
  }

  // Add source for preview lines
  const sourceExists = window.map.getSource('drag-preview-lines');
  console.log('[DRAG_PREVIEW] drag-preview-lines source exists:', !!sourceExists);

  if (!sourceExists) {
    console.log('[DRAG_PREVIEW] Creating drag-preview-lines source');
    window.map.addSource('drag-preview-lines', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    console.log('[DRAG_PREVIEW] drag-preview-lines source created');
  }

  // Find a good layer to insert before (above existing routes)
  const layers = window.map.getStyle().layers;
  let beforeLayer: string | undefined;

  // Look for first symbol layer or any layer with higher z-index
  for (const layer of layers) {
    if (layer.type === 'symbol' || layer.id.includes('marker')) {
      beforeLayer = layer.id;
      break;
    }
  }
  console.log('[DRAG_PREVIEW] Will insert layer before:', beforeLayer);

  // Add preview line layer (dashed gray line)
  const layerExists = window.map.getLayer('drag-preview-lines');
  console.log('[DRAG_PREVIEW] drag-preview-lines layer exists:', !!layerExists);

  if (!layerExists) {
    console.log('[DRAG_PREVIEW] Creating drag-preview-lines layer');
    window.map.addLayer({
      id: 'drag-preview-lines',
      type: 'line',
      source: 'drag-preview-lines',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#808080',
        'line-width': 4,
        'line-dasharray': [2, 2],
        'line-opacity': 0.8
      }
    }, beforeLayer);
    console.log('[DRAG_PREVIEW] drag-preview-lines layer created');
  }
}

// Update preview layers with new data
function updatePreviewLayers(lineStrings: LineStringFeature[]) {
  console.log('[DRAG_PREVIEW] updatePreviewLayers called with', lineStrings.length, 'lineStrings');
  if (!window.map) {
    console.log('[DRAG_PREVIEW] window.map not available');
    return;
  }

  // Update preview lines
  const lineSource = window.map.getSource('drag-preview-lines') as mapboxgl.GeoJSONSource;
  console.log('[DRAG_PREVIEW] lineSource exists:', !!lineSource);

  if (lineSource) {
    const features = lineStrings.map(ls => ls.toGeoJson());
    console.log('[DRAG_PREVIEW] Updating source with', features.length, 'features');
    lineSource.setData({
      type: 'FeatureCollection',
      features
    });
    console.log('[DRAG_PREVIEW] Source updated successfully');
  } else {
    console.log('[DRAG_PREVIEW] lineSource not found, cannot update');
  }
}

// Clear preview layers
function clearPreviewLayers() {
  console.log('[DRAG_PREVIEW] clearPreviewLayers called');
  if (!window.map) {
    console.log('[DRAG_PREVIEW] window.map not available');
    return;
  }

  const lineSource = window.map.getSource('drag-preview-lines') as mapboxgl.GeoJSONSource;
  console.log('[DRAG_PREVIEW] lineSource exists:', !!lineSource);

  if (lineSource) {
    console.log('[DRAG_PREVIEW] Clearing preview data');
    lineSource.setData({
      type: 'FeatureCollection',
      features: []
    });
    console.log('[DRAG_PREVIEW] Preview data cleared');
  }
}

// Calculate preview routes with GraphHopper
async function calculatePreviewRoutes(
  context: ActionContextType,
  markerIndex: number,
  routeId: string,
  position: [number, number],
  isSnap: boolean
): Promise<void> {
  console.log('[DRAG_PREVIEW] calculatePreviewRoutes called', { markerIndex, routeId, position, isSnap });
  const state = context.state;
  const route = state.routes[routeId];
  if (!route) {
    console.log('[DRAG_PREVIEW] calculatePreviewRoutes - route not found');
    return;
  }

  // Cancel any pending request
  if (state.dragPreview.pendingRequest) {
    state.dragPreview.pendingRequest.abort();
  }

  // Create new abort controller
  const controller = new AbortController();
  context.commit('setDragPreviewPendingRequest', controller);

  try {
    const markers = route.markers;
    const previewLineStrings: LineStringFeature[] = [];
    let snappedPosition: [number, number] | null = null;

    console.log('[DRAG_PREVIEW] calculatePreviewRoutes - isSnap:', isSnap);
    // If snap is enabled, get snapped position first
    if (isSnap) {
      // For now, we'll use the position as-is
      // In a real implementation, we'd call a snap endpoint
      snappedPosition = position;
      console.log('[DRAG_PREVIEW] calculatePreviewRoutes - snappedPosition set to:', snappedPosition);
    } else {
      snappedPosition = position;
      console.log('[DRAG_PREVIEW] calculatePreviewRoutes - snappedPosition set to (no snap):', snappedPosition);
    }

    // Update snapped position in state
    context.commit('updateDragPreviewSnappedPosition', snappedPosition);

    // Calculate preview routes for affected segments
    if (markerIndex === 0 && markers.length > 1) {
      // First marker - update first segment
      const nextMarker = markers[1];

      // Create temporary marker for snapped position
      const tempStartMarker = new MarkerFeature(
        new MarkerGeometry(snappedPosition),
        new MarkerProperties('temp-preview-start', isSnap, Date.now().toString(), 0)
      );

      const lineString = await snapPoints(
        tempStartMarker,
        nextMarker,
        state.routePreference
      );
      previewLineStrings.push(lineString);
    } else if (markerIndex === markers.length - 1 && markers.length > 1) {
      // Last marker - update last segment
      const prevMarker = markers[markerIndex - 1];

      // Create temporary marker for snapped position
      const tempEndMarker = new MarkerFeature(
        new MarkerGeometry(snappedPosition),
        new MarkerProperties('temp-preview-end', isSnap, Date.now().toString(), 0)
      );

      const lineString = await snapPoints(
        prevMarker,
        tempEndMarker,
        state.routePreference
      );
      previewLineStrings.push(lineString);
    } else if (markerIndex > 0 && markerIndex < markers.length - 1) {
      // Middle marker - update both adjacent segments
      const prevMarker = markers[markerIndex - 1];
      const nextMarker = markers[markerIndex + 1];

      // Create temporary marker for snapped position
      const tempMidMarker = new MarkerFeature(
        new MarkerGeometry(snappedPosition),
        new MarkerProperties('temp-preview-mid', isSnap, Date.now().toString(), 0)
      );

      // Previous segment
      const prevLineString = await snapPoints(
        prevMarker,
        tempMidMarker,
        state.routePreference
      );
      previewLineStrings.push(prevLineString);

      // Next segment
      const nextLineString = await snapPoints(
        tempMidMarker,
        nextMarker,
        state.routePreference
      );
      previewLineStrings.push(nextLineString);
    }

    // Check if we're still dragging before updating
    // (in case drag ended while we were calculating)
    if (!context.state.dragPreview.isDragging) {
      console.log('[DRAG_PREVIEW] calculatePreviewRoutes - drag ended, skipping update');
      return;
    }

    // Update preview in state
    context.commit('updateDragPreviewLineStrings', previewLineStrings);

    // Update map layers only if still dragging
    if (context.state.dragPreview.isDragging) {
      updatePreviewLayers(previewLineStrings);
    }

  } catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('Error calculating preview routes:', error);
    }
  }
}

// Calculate preview segments for a stage-connector drag: the previous
// route's LAST segment and the next route's FIRST segment, both re-routed
// to the cursor position.
async function calculateConnectorPreview(
  context: ActionContextType,
  position: [number, number]
): Promise<void> {
  const state = context.state;
  const connector = state.dragPreview.connector;
  if (!connector) return;

  const prevRoute = state.routes[connector.prevRouteKey];
  const nextRoute = state.routes[connector.nextRouteKey];
  if (!prevRoute || !nextRoute || prevRoute.markers.length < 2 || nextRoute.markers.length < 2) return;

  // Cancel any pending request
  if (state.dragPreview.pendingRequest) {
    state.dragPreview.pendingRequest.abort();
  }
  const controller = new AbortController();
  context.commit('setDragPreviewPendingRequest', controller);

  try {
    context.commit('updateDragPreviewSnappedPosition', position);

    const tempMarker = new MarkerFeature(
      new MarkerGeometry(position),
      new MarkerProperties('temp-preview-connector', state.isSnap, Date.now().toString(), 0)
    );
    const prevAnchor = prevRoute.markers[prevRoute.markers.length - 2];
    const nextAnchor = nextRoute.markers[1];

    let previewLineStrings: LineStringFeature[];
    if (state.isSnap) {
      previewLineStrings = await Promise.all([
        snapPoints(prevAnchor, tempMarker, state.routePreference),
        snapPoints(tempMarker, nextAnchor, state.routePreference),
      ]);
    } else {
      // Snap disabled — preview straight segments, no routing calls
      previewLineStrings = [
        LineStringFeature.straightLine(prevAnchor, tempMarker),
        LineStringFeature.straightLine(tempMarker, nextAnchor),
      ];
    }

    // Drag may have ended (or switched target) while we were routing
    if (!context.state.dragPreview.isDragging || !context.state.dragPreview.connector) return;

    context.commit('updateDragPreviewLineStrings', previewLineStrings);
    updatePreviewLayers(previewLineStrings);
  } catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('Error calculating connector preview:', error);
    }
  }
}

// Debounced version of calculatePreviewRoutes
const debouncedCalculatePreview = debounce(calculatePreviewRoutes, DRAG_PREVIEW_DEBOUNCE_MS);

// Debounced version of calculateConnectorPreview
const debouncedCalculateConnectorPreview = debounce(calculateConnectorPreview, DRAG_PREVIEW_DEBOUNCE_MS);

// --- Day flags ---
// The line the two days either side of the dragged flag walk, worked out once
// when the drag starts: the flag follows it, and a drop off it gets a detour.
let flagDrag: { connectorId: string; trail: MeasuredLine } | null = null;

const connectorIdOf = (connector: { prevRouteKey: string; nextRouteKey: string }) =>
  `${connector.prevRouteKey}::${connector.nextRouteKey}`;

function flagLineFor(state: PlanTrailState): MeasuredLine | null {
  const connector = state.dragPreview.connector;
  return connector && flagDrag?.connectorId === connectorIdOf(connector) ? flagDrag.trail : null;
}

// --- Preview of the detour out to a flag dropped off the line ---
// Shown straight at once, then swapped for the GraphHopper route between the
// same two points once the cursor settles — as free mode previews. Only the
// nearest rejoin point is routed here; the drop itself still tries several
// (gateOntoLine), so occasionally it settles on a different, shorter way back.
let detourRouteToken = 0;

function previewMarker(coord: number[], id: string, isSnap: boolean): MarkerFeature {
  return new MarkerFeature(new MarkerGeometry([...coord]), new MarkerProperties(id, isSnap, Date.now().toString(), 0));
}

function showDetourLeg(context: ActionContextType, lines: LineStringFeature[]) {
  context.commit('updateDragPreviewLineStrings', lines);
  updatePreviewLayers(lines);
}

type DetourLeg = { from: number[]; to: number[] };

async function routeDetourLeg(
  context: ActionContextType,
  segment: DetourLeg,
  token: number
): Promise<void> {
  const { state } = context;
  try {
    const routed = await snapPoints(
      previewMarker(segment.from, 'temp-preview-from', true),
      previewMarker(segment.to, 'temp-preview-to', true),
      state.routePreference
    );
    // A newer cursor position (or the drop) has superseded this request.
    if (token !== detourRouteToken || !state.dragPreview.isDragging) return;
    // The way back stops where it first meets the line, as the drop does.
    const trail = flagLineFor(state);
    const cropped = trail ? cropDetourAtTrail(trail, routed.lineStringGeometry.coordinates, 0, Infinity) : null;
    showDetourLeg(context, [cropped ? LineStringFeature.coordsOnlyFeature(cropped.coordinates) : routed]);
  } catch (err) {
    // No route between the two points — the straight line already shown stays.
    console.warn('[DRAG_PREVIEW] no route for the detour preview:', err);
  }
}

const debouncedRouteDetourLeg = debounce(routeDetourLeg, DRAG_PREVIEW_DEBOUNCE_MS);

/**
 * Draws the detour preview for `segment` (or clears it for null): straight
 * now, routed once the cursor settles when snapping is on.
 */
function drawDetourLeg(context: ActionContextType, segment: DetourLeg | null) {
  const token = ++detourRouteToken;
  debouncedRouteDetourLeg.cancel();
  if (!segment) {
    showDetourLeg(context, []);
    return;
  }
  const { isSnap } = context.state;
  showDetourLeg(context, [LineStringFeature.straightLine(
    previewMarker(segment.from, 'temp-preview-from', isSnap),
    previewMarker(segment.to, 'temp-preview-to', isSnap)
  )]);
  if (isSnap) debouncedRouteDetourLeg(context, segment, token);
}

// Cancel any pending preview calculations
function cancelPendingCalculations(state: PlanTrailState) {
  console.log('[DRAG_PREVIEW] cancelPendingCalculations called');

  // Cancel debounced calls
  debouncedCalculatePreview.cancel();
  debouncedCalculateConnectorPreview.cancel();
  debouncedRouteDetourLeg.cancel();
  detourRouteToken++;

  // Abort any in-flight requests
  if (state.dragPreview.pendingRequest) {
    state.dragPreview.pendingRequest.abort();
  }
}

export const dragPreviewActions = {
  // Initialize drag preview
  initializeDragPreview({ commit }: ActionContextType, payload: {
    markerIndex: number;
    routeId: string;
    position: [number, number]
  }) {
    console.log('[DRAG_PREVIEW] initializeDragPreview called', payload);
    // Create preview layers if they don't exist
    createPreviewLayers();

    // Start drag preview state
    commit('startDragPreview', payload);
    console.log('[DRAG_PREVIEW] Drag preview initialized');
  },

  // Update drag preview during drag
  async updateDragPreview(
    context: ActionContextType,
    position: [number, number]
  ) {
    console.log('[DRAG_PREVIEW] updateDragPreview called', position);
    const { dragPreview, isSnap } = context.state;

    if (!dragPreview.isDragging ||
        dragPreview.markerIndex === null ||
        !dragPreview.routeId) {
      console.log('[DRAG_PREVIEW] updateDragPreview - not in dragging state');
      return;
    }

    console.log('[DRAG_PREVIEW] updateDragPreview - updating position');
    // Update preview position
    context.commit('updateDragPreviewPosition', position);

    // Calculate preview routes (debounced)
    console.log('[DRAG_PREVIEW] updateDragPreview - calling debouncedCalculatePreview');
    await debouncedCalculatePreview(
      context,
      dragPreview.markerIndex,
      dragPreview.routeId,
      position,
      isSnap
    );
  },

  // Finalize drag preview
  async finalizeDragPreview({ state, commit, dispatch }: ActionContextType) {
    const { dragPreview } = state;

    // Cancel any pending calculations first
    cancelPendingCalculations(state);

    // Always clear preview layers and end drag preview state
    const cleanup = () => {
      clearPreviewLayers();
      commit('endDragPreview');
    };

    if (!dragPreview.isDragging ||
        dragPreview.markerIndex === null ||
        !dragPreview.routeId) {
      cleanup();
      return false; // Return false to indicate preview wasn't applied
    }

    // If we don't have a snapped position yet (drag was too quick), return false
    if (!dragPreview.snappedPosition) {
      cleanup();
      return false; // Return false to indicate preview wasn't applied
    }

    // Apply the snapped position to the actual marker
    const route = state.routes[dragPreview.routeId];
    if (route && route.markers[dragPreview.markerIndex]) {
      const marker = route.markers[dragPreview.markerIndex];

      // Capture old position for the undo command
      const oldPosition = [...marker.markerGeometry.coordinates];

      // Update marker position to snapped position
      marker.markerGeometry.coordinates = [...dragPreview.snappedPosition];

      // Call markerMoved with oldPosition so it creates a MoveMarkerCommand.
      // Scoped to the dragged route — it may not be the active one.
      await dispatch('markerMoved', { marker, oldPosition, routeKey: dragPreview.routeId });
    } else {
      cleanup();
      return false;
    }

    // Clear preview
    cleanup();
    return true; // Return true to indicate preview was applied
  },

  // Hide the network preview line WITHOUT ending the drag session — used
  // while a stage marker is snapped to an attach target, so the competing
  // gray "nearest route" preview doesn't flicker underneath it. Moving back
  // off the target lets updateDragPreview redraw as normal.
  hideDragPreviewLine({ state }: ActionContextType) {
    cancelPendingCalculations(state);
    clearPreviewLayers();
  },

  // Cancel drag preview
  cancelDragPreview({ state, commit }: ActionContextType) {
    console.log('[DRAG_PREVIEW] cancelDragPreview called');
    // Cancel any pending calculations
    cancelPendingCalculations(state);
    clearPreviewLayers();
    commit('endDragPreview');
  },

  // ---------------------------------------------------------------------
  // Stage-connector dragging — one handle that moves the previous route's
  // end marker and the next route's start marker together.
  // ---------------------------------------------------------------------

  initializeConnectorDrag({ state, commit }: ActionContextType, payload: {
    prevRouteKey: string;
    nextRouteKey: string;
    position: [number, number];
  }) {
    createPreviewLayers();
    commit('startConnectorDragPreview', payload);

    // A flag between two days slides along the line they walk.
    flagDrag = null;
    const prev = state.routes[payload.prevRouteKey];
    const next = state.routes[payload.nextRouteKey];
    if (prev && next && (prev.day ?? 1) !== (next.day ?? 1)) {
      const line = dayFlagLine(prev, next);
      if (line) flagDrag = { connectorId: connectorIdOf(payload), trail: line.trail };
    }
  },

  /**
   * Follows a connector drag to `position`, returning where the handle should
   * be drawn: a day's flag sits on the line while the cursor is near it, and
   * otherwise previews the detour a drop there would add.
   */
  async updateConnectorDragPreview(context: ActionContextType, position: [number, number]): Promise<[number, number]> {
    const { dragPreview } = context.state;
    if (!dragPreview.isDragging || !dragPreview.connector) return position;

    context.commit('updateDragPreviewPosition', position);

    const flagLine = flagLineFor(context.state);
    if (flagLine) {
      context.commit('updateDragPreviewSnappedPosition', position);
      const nearest = nearestAlongLine(flagLine, position);
      if (nearest.offLineM <= ON_TRAIL_M) {
        drawDetourLeg(context, null);
        return [nearest.point[0], nearest.point[1]];
      }
      drawDetourLeg(context, { from: nearest.point, to: position });
      return position;
    }

    debouncedCalculateConnectorPreview(context, position);
    return position;
  },

  /**
   * Apply a connector drag: move both endpoint markers to the drop point,
   * re-route the previous route's last segment and the next route's first
   * segment, and push a single compound undo command covering both routes.
   *
   * Returns true when the drag was applied, false when it was abandoned
   * (nothing was moved, or routing failed and everything was reverted).
   */
  async finalizeConnectorDrag(
    { state, commit, dispatch }: ActionContextType,
    payload?: { fallbackPosition?: [number, number] | null }
  ) {
    const { dragPreview } = state;
    const connector = dragPreview.connector;

    cancelPendingCalculations(state);

    const cleanup = () => {
      clearPreviewLayers();
      commit('endDragPreview');
    };

    if (!dragPreview.isDragging || !connector) {
      cleanup();
      return false;
    }

    const target: [number, number] | null =
      dragPreview.snappedPosition ?? dragPreview.previewPosition ?? payload?.fallbackPosition ?? null;

    const prevRoute = state.routes[connector.prevRouteKey];
    const nextRoute = state.routes[connector.nextRouteKey];
    if (!target || !prevRoute || !nextRoute ||
        prevRoute.markers.length < 2 || nextRoute.markers.length < 2) {
      cleanup();
      return false;
    }

    const prevMarkerIndex = prevRoute.markers.length - 1;
    const prevMarker = prevRoute.markers[prevMarkerIndex];
    const nextMarker = nextRoute.markers[0];
    const prevLineStringIndex = prevRoute.lineStrings.length - 1;

    // The flag between two days: it slides along the line they walk, and a
    // drop off the line asks before sending the night off the trail. (Two
    // routes of the same day join at an ordinary point on the line, which
    // moves like a control point — below.)
    if ((prevRoute.day ?? 1) !== (nextRoute.day ?? 1)) {
      const move = { prevRouteKey: connector.prevRouteKey, nextRouteKey: connector.nextRouteKey, targetCoord: target };
      let result = await dispatch('moveDayFlag', move);
      let asked = false;
      if (result === 'needs-detour') {
        asked = true;
        const addDetour = await askToAddDetour(getMap(), target, {
          question: i18n.global.t('plan_trail.day_flags.detour_question'),
          add: i18n.global.t('plan_trail.day_flags.detour_add'),
          cancel: i18n.global.t('plan_trail.day_flags.detour_cancel'),
        });
        result = addDetour ? await dispatch('moveDayFlag', { ...move, allowDetour: true }) : 'unchanged';
      }
      flagDrag = null;
      // 'unchanged' without asking means the two days have no line to slide
      // along (a route that never got one) — move the handle like a point.
      if (result !== 'unchanged' || asked) {
        cleanup();
        await dispatch('updateRouteAndMarkers');
        return result === 'moved';
      }
    }

    // Capture state for the undo command (and for reverting on failure)
    const oldPrevPosition = [...prevMarker.markerGeometry.coordinates];
    const oldNextPosition = [...nextMarker.markerGeometry.coordinates];
    const oldPrevLine = prevRoute.lineStrings[prevLineStringIndex]
      ? JSON.parse(JSON.stringify(prevRoute.lineStrings[prevLineStringIndex])) : null;
    const oldNextLine = nextRoute.lineStrings[0]
      ? JSON.parse(JSON.stringify(nextRoute.lineStrings[0])) : null;

    try {
      // Move both markers to the drop point before routing so the routing
      // inputs reflect the new shared endpoint
      prevMarker.markerGeometry.coordinates = [...target];
      nextMarker.markerGeometry.coordinates = [...target];
      prevMarker.markerProperties.isSnap = state.isSnap;
      nextMarker.markerProperties.isSnap = state.isSnap;

      const prevAnchor = prevRoute.markers[prevMarkerIndex - 1];
      const nextAnchor = nextRoute.markers[1];

      let newPrevLine: LineStringFeature;
      let newNextLine: LineStringFeature;

      if (state.isSnap) {
        // Route the incoming segment first, then pin both markers to its
        // snapped network endpoint so the outgoing segment starts from the
        // exact same point — this is what makes the join truly shared.
        //
        // GraphHopper may genuinely have no route to the drop point (e.g. a
        // POI off any routable way) — fall back to a straight, flagged
        // off-network line for whichever segment fails, rather than letting
        // it reject into the outer catch below, which reverts BOTH markers
        // back to their old positions and discards the whole drag.
        newPrevLine = await snapPoints(prevAnchor, prevMarker, state.routePreference).catch(async (err) => {
          console.error('snapPoints failed for connector drag (incoming segment), falling back to a straight line:', err);
          const fallback = await hiikerElevation(prevAnchor, prevMarker);
          (fallback.lineStringProperties as any).offNetworkFull = true;
          return fallback;
        });
        const coords = newPrevLine.lineStringGeometry.coordinates;
        const snappedEnd = coords[coords.length - 1];
        if (snappedEnd) {
          prevMarker.markerGeometry.coordinates = [...snappedEnd];
          nextMarker.markerGeometry.coordinates = [...snappedEnd];
        }
        newNextLine = await snapPoints(nextMarker, nextAnchor, state.routePreference).catch(async (err) => {
          console.error('snapPoints failed for connector drag (outgoing segment), falling back to a straight line:', err);
          const fallback = await hiikerElevation(nextMarker, nextAnchor);
          (fallback.lineStringProperties as any).offNetworkFull = true;
          return fallback;
        });
      } else {
        // Snap disabled — straight segments with real elevations
        newPrevLine = await hiikerElevation(prevAnchor, prevMarker);
        newNextLine = await hiikerElevation(nextMarker, nextAnchor);
      }

      newPrevLine.lineStringProperties.id = generateID();
      newNextLine.lineStringProperties.id = generateID();

      if (prevLineStringIndex >= 0) {
        prevRoute.lineStrings.splice(prevLineStringIndex, 1, newPrevLine);
      } else {
        prevRoute.lineStrings.push(newPrevLine);
      }
      if (nextRoute.lineStrings.length > 0) {
        nextRoute.lineStrings.splice(0, 1, newNextLine);
      } else {
        nextRoute.lineStrings.push(newNextLine);
      }

      state.undoManager.push(new MoveConnectorCommand([
        {
          routeKey: connector.prevRouteKey,
          markerIndex: prevMarkerIndex,
          oldPosition: oldPrevPosition,
          newPosition: [...prevMarker.markerGeometry.coordinates],
          lineStringIndex: Math.max(prevLineStringIndex, 0),
          oldLineString: oldPrevLine,
          newLineString: JSON.parse(JSON.stringify(newPrevLine)),
        },
        {
          routeKey: connector.nextRouteKey,
          markerIndex: 0,
          oldPosition: oldNextPosition,
          newPosition: [...nextMarker.markerGeometry.coordinates],
          lineStringIndex: 0,
          oldLineString: oldNextLine,
          newLineString: JSON.parse(JSON.stringify(newNextLine)),
        },
      ]));

      prevRoute.updated_at = new Date();
      nextRoute.updated_at = new Date();
      state._statsVersion++;
      announceRouteChanged();

      cleanup();
      await dispatch('updateRouteAndMarkers');
      return true;
    } catch (error) {
      console.error('Error finalizing connector drag:', error);
      // Revert marker moves — routes must stay consistent with their lines
      prevMarker.markerGeometry.coordinates = oldPrevPosition;
      nextMarker.markerGeometry.coordinates = oldNextPosition;
      cleanup();
      await dispatch('updateRouteAndMarkers');
      return false;
    }
  },

  /**
   * "Disconnect stages" (connector context menu / day-list annotation):
   * flag the pair in disconnectedStageIds so the shared connector handle
   * disappears, and nudge the two previously-shared markers apart into a
   * side-by-side position so each is individually grabbable. The route
   * LINES are not touched — no re-routing, no redraw of the geometry; only
   * the marker handles separate. One undo restores positions + connection.
   *
   * Returns true when the stages were disconnected.
   */
  async disconnectStages(
    { state, dispatch }: ActionContextType,
    payload: { prevRouteKey: string; nextRouteKey: string }
  ) {
    const prevRoute = state.routes[payload.prevRouteKey];
    const nextRoute = state.routes[payload.nextRouteKey];
    if (!prevRoute || !nextRoute ||
        prevRoute.markers.length < 1 || nextRoute.markers.length < 1) {
      return false;
    }

    const connectorId = `${payload.prevRouteKey}::${payload.nextRouteKey}`;
    if (state.disconnectedStageIds.includes(connectorId)) return false;

    const prevMarkerIndex = prevRoute.markers.length - 1;
    const prevMarker = prevRoute.markers[prevMarkerIndex];
    const nextMarker = nextRoute.markers[0];
    const oldPrevPosition = [...prevMarker.markerGeometry.coordinates];
    const oldNextPosition = [...nextMarker.markerGeometry.coordinates];

    // Side-by-side split: ~12 screen px each way at the current zoom
    // (clamped to 5–30 m) so the handles come apart visibly wherever the
    // user is zoomed. Markers only — the lines stay put.
    let halfMeters = 10;
    try {
      const zoom = getMap().getZoom();
      const metersPerPixel = 156543.03392 *
        Math.cos(oldPrevPosition[1] * Math.PI / 180) / Math.pow(2, zoom);
      halfMeters = Math.min(Math.max(12 * metersPerPixel, 5), 30);
    } catch (_) { /* map not ready — keep the 10 m fallback */ }

    // Endpoints already further apart than the split distance stay put —
    // disconnecting then only removes the shared handle
    if (haversineMeters(oldPrevPosition, oldNextPosition) >= halfMeters * 2) {
      state.disconnectedStageIds.push(connectorId);
      state.undoManager.push(new DisconnectStagesCommand([], connectorId));
      await dispatch('updateRouteAndMarkers');
      return true;
    }

    // Moving markers edits both routes — referenced hikes get the same
    // warning that gates their marker drags
    const win = window as any;
    const referenced = [prevRoute, nextRoute].filter(route => route.isReference);
    if (referenced.length > 0 &&
        typeof win.canEditReference === 'function' &&
        referenced.some(route => !win.canEditReference(route))) {
      if (typeof win.checkReferenceEditWarning === 'function') {
        win.checkReferenceEditWarning(referenced[0]);
      }
      return false;
    }

    // Split east–west around the shared point: prev end west, next start east
    const midLng = (oldPrevPosition[0] + oldNextPosition[0]) / 2;
    const midLat = (oldPrevPosition[1] + oldNextPosition[1]) / 2;
    const dLng = halfMeters / (111320 * Math.cos(midLat * Math.PI / 180));
    prevMarker.markerGeometry.coordinates = [midLng - dLng, midLat];
    nextMarker.markerGeometry.coordinates = [midLng + dLng, midLat];

    state.disconnectedStageIds.push(connectorId);
    state.undoManager.push(new DisconnectStagesCommand([
      {
        routeKey: payload.prevRouteKey,
        markerIndex: prevMarkerIndex,
        oldPosition: oldPrevPosition,
        newPosition: [...prevMarker.markerGeometry.coordinates],
        lineStringIndex: -1,
        oldLineString: null,
        newLineString: null,
      },
      {
        routeKey: payload.nextRouteKey,
        markerIndex: 0,
        oldPosition: oldNextPosition,
        newPosition: [...nextMarker.markerGeometry.coordinates],
        lineStringIndex: -1,
        oldLineString: null,
        newLineString: null,
      },
    ], connectorId));

    prevRoute.updated_at = new Date();
    nextRoute.updated_at = new Date();

    // Layer refresh only — swaps the shared handle for the two side-by-side
    // endpoint markers. Route geometry is untouched.
    await dispatch('updateRouteAndMarkers');
    return true;
  },

  /**
   * "Join these days" (the flag's map context menu, or dropping a separated
   * day's end onto the next day's start): the earlier day walks on to where
   * the later one starts along a short new leg — see bridgeDays. Neither day's
   * existing line is re-routed, so a gap in an official hike can be closed
   * without disturbing either day. Also re-enables the shared flag if the pair
   * had been separated, with its own undo entry for that.
   *
   * Returns true when the days were joined.
   */
  async reconnectStages(
    { state, dispatch }: ActionContextType,
    payload: { prevRouteKey: string; nextRouteKey: string }
  ) {
    const prevRoute = state.routes[payload.prevRouteKey];
    const nextRoute = state.routes[payload.nextRouteKey];
    if (!prevRoute || !nextRoute ||
        prevRoute.markers.length < 1 || nextRoute.markers.length < 1) {
      return false;
    }

    // Joining edits the routes like any other drag — referenced hikes get
    // the same warning that gates their marker drags
    const win = window as any;
    const referenced = [prevRoute, nextRoute].filter(route => route.isReference);
    if (referenced.length > 0 &&
        typeof win.canEditReference === 'function' &&
        referenced.some(route => !win.canEditReference(route))) {
      if (typeof win.checkReferenceEditWarning === 'function') {
        win.checkReferenceEditWarning(referenced[0]);
      }
      return false;
    }

    const connectorId = `${payload.prevRouteKey}::${payload.nextRouteKey}`;
    const wasExcluded = state.disconnectedStageIds.includes(connectorId);
    const prevBefore = JSON.parse(JSON.stringify(prevRoute));

    if (!(await bridgeDays(state, payload.prevRouteKey, payload.nextRouteKey))) return false;

    state.undoManager.push(new RouteSnapshotsCommand('JOIN_DAYS', [
      { routeKey: payload.prevRouteKey, before: prevBefore, after: JSON.parse(JSON.stringify(prevRoute)) },
    ]));
    if (wasExcluded) {
      state.undoManager.push(new ReconnectStagesCommand(connectorId));
    }

    await dispatch('updateRouteAndMarkers');
    return true;
  }
};
